import type { ReactNode } from 'react';

import { twcn } from '@web/ui/core/utils';

import { ColumnCard } from './column_card';
import type { BoardColumnRow } from '../hooks/use_org_board_config_page';

export interface BoardGroupProps {
  title: string;
  sub?: string;
  cols: readonly BoardColumnRow[];
  /** Extra block rendered below a divider (e.g. the retention selector). */
  children?: ReactNode;
}

/**
 * A board's configuration panel: heading + description over a responsive
 * grid of ColumnCards. The grid is 3 or 4 columns on desktop, falling to
 * 2 then 1 as the viewport narrows so cards never get squeezed. An
 * optional footer block sits below a divider.
 */
export function BoardGroup({
  title,
  sub,
  cols,
  children,
}: Readonly<BoardGroupProps>): React.JSX.Element {
  const gridCols =
    cols.length === 3
      ? 'lg:grid-cols-3 md:grid-cols-2 grid-cols-1'
      : 'lg:grid-cols-4 md:grid-cols-2 grid-cols-1';

  return (
    <section className="mb-6 rounded-lg border border-border-soft bg-surface px-5 py-5">
      <header className="mb-4 flex flex-col gap-1">
        <h2 className="m-0 text-card-title font-medium tracking-display-soft text-foreground">
          {title}
        </h2>
        {sub ? <p className="m-0 text-meta leading-relaxed text-muted-foreground">{sub}</p> : null}
      </header>

      <div className={twcn('grid gap-3.5', gridCols)}>
        {cols.map((col, i) => (
          <ColumnCard key={col.stableId} row={col} position={i + 1} />
        ))}
      </div>

      {children ? (
        <>
          <div className="my-5 border-t border-border-soft" />
          {children}
        </>
      ) : null}
    </section>
  );
}
